/**
 * Custom currency picker (2026-09-02, updated 2026-08-29, 2027-03-05). Replaces the previous
 * BUCKS-based integration entirely - no third-party widget, no relocation, no polling, no
 * window.bucksCC. Owns every real native <select data-currency-picker> (see
 * snippets/header-drawer.liquid - normally one, but two when Basic mode's own desktop-only merged
 * row renders alongside the mobile row, each with its own unique id), always in normal document
 * flow inside the menu drawer's own content (visible only while the drawer is open, exactly like
 * the existing language selector right above it - same mechanism, same CSS, no extra work needed
 * here to satisfy that).
 *
 * On change, this only ever does two things: set html[data-display-currency] (the same attribute
 * layout/theme.liquid's own inline script sets synchronously before first paint) and persist the
 * choice to localStorage, so every already-rendered price on the page (see
 * snippets/localized-price.liquid) updates immediately via CSS alone - never by rewriting price
 * text, never by fetching/recalculating anything.
 *
 * Root cause of the 2026-08-29 desync bug: a one-shot select.value assignment at init time is not
 * enough - confirmed live (Playwright instrumentation on the HTMLSelectElement.prototype.value
 * setter) that this script's own init() correctly sets select.value to match the html attribute
 * exactly once per load, yet the select's actual selectedness later reverts to its first option
 * ("usd") with no further JS ever calling the value setter - i.e. something below the scripted
 * layer (the browser's own native form-control state handling for reload/session-history
 * navigation) overwrites it afterward. Re-running the same direct assignment later - on the menu
 * drawer's own native `toggle` event - was verified live to survive that reset, since it runs after
 * whatever native mechanism causes it. html[data-display-currency] remains the single source of
 * truth throughout; this never reads from or writes to the select except to mirror that attribute
 * and to react to a real user change.
 *
 * Owner request (2027-03-05): querySelector -> querySelectorAll, so a second simultaneously-
 * rendered picker (Basic mode's own desktop row, shown/hidden by CSS rather than only one ever
 * existing in the DOM) is also wired up and kept in sync, not silently ignored. Changing either
 * one applies the choice (same single source of truth as before) and immediately re-syncs every
 * other picker on the page, so two open copies can never show different selected values.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'display_currency';

  function apply(currency) {
    document.documentElement.setAttribute('data-display-currency', currency);
    try {
      window.localStorage.setItem(STORAGE_KEY, currency);
    } catch (e) {}
  }

  function sync(select) {
    var current = document.documentElement.getAttribute('data-display-currency') || 'usd';
    if (select.value !== current) select.value = current;
  }

  function init() {
    var selects = document.querySelectorAll('[data-currency-picker]');
    if (!selects.length) return;

    var syncAll = function () {
      selects.forEach(sync);
    };

    syncAll();

    selects.forEach(function (select) {
      select.addEventListener('change', function () {
        apply(select.value);
        syncAll();
      });
    });

    var detailsSeen = [];
    selects.forEach(function (select) {
      var details = select.closest('details');
      if (details && detailsSeen.indexOf(details) === -1) {
        detailsSeen.push(details);
        details.addEventListener('toggle', syncAll);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
